import React, { useState, useEffect }from 'react'
import { useSelector } from 'react-redux'
import { Typography, Spinner, useDimensions } from '@telavita-core/react-design-kit'

import ReservationService from 'services/reservation'

import { UserData } from 'models/Auth'
import { getProfessionalUuidFromAuthUserData } from 'utils/getProfessionalUuidFromAuthUserData'

import { CreateReservationModal } from 'containers/ModalManager/Modals/CreateReservationModal'
import { useOctadeskChat } from 'hooks'

import Row from 'components/Row'
import Tabs from 'components/Tabs'
import FloatActionButtonDesktop from 'components/FloatActionButtonDesktop'
import FloatActionButtonMobile from 'components/FloatActionButtonMobile'

import { DayHeader, ReservationListItem, ReservationEmptyState } from './components'

import { Reservation } from 'models/Reservation'

export enum ReservationType {
  RECURRENT = 'RECURRENT',
  NON_RECURRENT = 'NON_RECURRENT',
}

enum RecurrenceType {
  WEEKLY = 'Semanal',
  NON_RECURRENT = 'Avulsa',
}

const handleReservationData = (selectedTab, reservations) => {
  if (selectedTab === ReservationType.RECURRENT) {
    return Object.keys(reservations).map((weekday) => ({
      label: weekday,
      reservations: reservations[weekday].filter((reservation) => reservation.recurrence === RecurrenceType.WEEKLY)
    }))
  }

  if (selectedTab === ReservationType.NON_RECURRENT) {
    return [{
      reservations: Object.keys(reservations)
        .reduce((arr, weekday) => arr.concat(reservations[weekday]), [])
        .filter((reservation) => reservation.recurrence === RecurrenceType.NON_RECURRENT)
    }]
  }
}

const ProfessionalReservations: React.FC = () => {
  const { isMobile } = useDimensions()
  const [loading, setLoading] = useState(true)
  const [reservations, setReservations] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [createReservationModal, setCreateReservationModal] = useState(false)
  const [selectedTab, setSelectedTab] = useState<ReservationType>(ReservationType.RECURRENT)
  
  const username = useSelector((state: UserData) => state.auth.userData.session.username)
  const professionalUuid = useSelector((state: UserData) =>
    getProfessionalUuidFromAuthUserData(state.auth.userData)
  )

  useOctadeskChat({
    hide: isMobile,
  })

  const onRefresh = () => setRefresh(true)

  const showCreateReservationModal = () => setCreateReservationModal(true)
  const closeCreateReservationModal = () => {
    setCreateReservationModal(false)
    onRefresh()
  }

  useEffect(() => {
    setLoading(true)
    ReservationService.getReservations({ username, isProfessional: true }, true)
      .then(reservations => {
        setRefresh(false)
        setLoading(false)
        setReservations(reservations as Reservation[])
      })
      .catch(() => {
        setRefresh(false)
        setLoading(false)
        setReservations([])
      })
  }, [username, refresh])

  const filterByTab = (reservation, selectedTab) => {
    if (selectedTab === ReservationType.RECURRENT && reservation.recurrence === RecurrenceType.WEEKLY) {
      return true
    }
    if (selectedTab === ReservationType.NON_RECURRENT && reservation.recurrence === RecurrenceType.NON_RECURRENT) {
      return true
    }
    return false
  }

  const hasReservation = Object?.keys(reservations)
    .map(weekday => reservations[weekday].filter(reservation => filterByTab(reservation, selectedTab)))
    .flatMap(reservations => reservations)

  const formattedReservations = handleReservationData(selectedTab, reservations)
  const renderReservations = () => {
    return formattedReservations.map(reservation => {
      if (reservation.reservations.length === 0) {
        return null
      }

      const sortReservationsByDate = reservation.reservations?.sort((a, b) => {
        return a.startDate - b.startDate
      })

      return (
        <div key={reservation.label}>
          {reservation.label && <DayHeader day={reservation.label} />}
          {sortReservationsByDate.map((reservation, index) => {
            return (
              <ReservationListItem
                hideBorderTop={selectedTab === ReservationType.NON_RECURRENT && index === 0}
                key={reservation.uuid}
                reservation={reservation}
                onRefresh={onRefresh}
              />
            )
          })}
        </div>
      )
    })
  }
  
  return (
    <>
      <div className="ProfessionalReservations">

        <Row
          className="Dashboard__Header"
          align={!isMobile ? 'center' : 'flex-start'}
          direction='row'
          height='100%'
          id=''
          justify={!isMobile ? 'space-between' : 'flex-start'}
          width='100%'
          isMansory={false}
          margin='0'
          maxWidth={!isMobile ? '100%' : 'auto'}
          onClick={() => null}
          padding='0'
          position='relative'
          wrap='nowrap'
        >
          <Typography variant={!isMobile ? 'header2' : 'header4'}>
            Reservas
          </Typography>
          
          {isMobile 
            ? (
              <FloatActionButtonMobile
                hasTitle={false}
                iconName='Positive'
                onClickFab={showCreateReservationModal}
                type='primary'
                title='Adicionar'
              />
            )
            : (
              <FloatActionButtonDesktop
                hasIcon
                iconName='Positive'
                iconViewBox='0 0 24 24'
                showInfo={false}
                buttonId=''
                infoText=''
                value={0}
                onClickFab={showCreateReservationModal}
                title='Adicionar'
                type='primary'
              />
            )
          }
        </Row>

        <Tabs
          tabId='ProfessionalReservations'
          tabs={[
            { title: 'Recorrentes', code: ReservationType.RECURRENT, subtitle: 'Se repetem toda semana' },
            { title: 'Avulsas', code: ReservationType.NON_RECURRENT, subtitle: 'Não se repetem' },
          ]}
          selectedItem={selectedTab}
          onSelectTab={setSelectedTab}
          style={{ marginBottom: selectedTab === ReservationType.RECURRENT ? '30px' : '15px' }}
        />

        {
          loading &&
          <div className='ProfessionalReservations__spinner'>
            <Spinner />
          </div>
        }

        {!loading && hasReservation?.length === 0 &&
          <ReservationEmptyState selectedTab={selectedTab} />
        }
 
        {!loading && Object.keys(reservations).length > 0 && (
          <div className='ProfessionalReservations__listItemWrapper'>
            {renderReservations()}
          </div>
        )}
        
      </div>
      {createReservationModal && (
        <CreateReservationModal 
          professionalUsername={username}
          professionalUuid={professionalUuid}
          onCloseModal={closeCreateReservationModal}
        />
      )}
    </>
  )
}

export { ProfessionalReservations }
